import {ElementRef, inject, Injectable} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_MATERIALS} from "@common/services/logging"
import {ActivatedRoute, Router} from "@angular/router"
import {PermissionsService} from "@app/common/services/permissions/permissions.service"
import {
    BatchMaterialOutputFragment,
    DataObjectDetailsForBatchDownloadServiceFragment as DataObjectDetailsFragment,
    GetBatchMaterialWithOutputGQL,
    ImageDownloadDetailsForBatchDownloadServiceFragment as ImageDownloadDetailsFragment,
    MaterialBatchOperationFragment,
} from "@app/platform/services/material/material-batch-operation.generated"
import type {MaterialMapsExporterCore} from "@cm/material-maps-exporter"
import {DownloadResolution, mapDownloadResolutionToImageResolution} from "@cm/utils/data-object"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {getDefaultPbrExportOptions} from "@common/helpers/material-maps-exporter"
import {BatchDownloadData, BatchItemOutcome, BatchMenuItem, ExtraBatchActionItem} from "@common/models/item/list-item"
import {MaterialAssetsRenderingService} from "@common/services/material-assets-rendering/material-assets-rendering.service"
import {NameAssetFromSchemaService} from "@common/services/name-asset-from-schema/name-asset-from-schema.service"
import {BasicTagInfoFragment} from "@common/services/tags/tags.generated"
import {UploadGqlService} from "@common/services/upload/upload.gql.service"
import {DataObjectAssignmentType, MaterialOutputType, MaterialExportOutcome} from "@generated"
import {FlatThumbnailOptionLabels, PbrExportOptionLabels} from "@labels"
import {
    MaterialsGridDeleteThumbnailGQL,
    MaterialsGridItemFragment,
    MaterialsGridUpdateMaterialGQL,
} from "@platform/components/materials/materials-grid/materials-grid.generated"
import {
    GetImageDownloadDetailsForBatchDownloadServiceGQL,
    MaterialBatchOperationMaterialWithMaterialRangeTagsGQL,
} from "@platform/services/material/material-batch-operation.generated"
import {MaterialOutputService} from "@platform/services/material/material-output.service"
import {MaterialOutputsPublishingService} from "@platform/services/material/material-outputs-publishing.service"
import {OrganizationsService} from "@app/common/services/organizations/organizations.service"

type GetBatchOperationsItems<T> = {create: T; delete: T; name?: T}

@Injectable()
export class MaterialBatchOperationService {
    readonly router = inject(Router)
    readonly route = inject(ActivatedRoute)
    readonly permission = inject(PermissionsService)
    readonly materialOutputService = inject(MaterialOutputService)
    readonly materialOutputsPublishingService = inject(MaterialOutputsPublishingService)
    private readonly logger: CmLogger = inject(CM_LOGGER_MATERIALS)
    $can = this.permission.$to

    readonly materialWithOutputGql = inject(GetBatchMaterialWithOutputGQL)
    readonly deleteThumbnailGql = inject(MaterialsGridDeleteThumbnailGQL)
    readonly materialWithRangeTagsGql = inject(MaterialBatchOperationMaterialWithMaterialRangeTagsGQL)
    readonly updateMaterialGql = inject(MaterialsGridUpdateMaterialGQL)
    readonly imageDownloadDetailsGql = inject(GetImageDownloadDetailsForBatchDownloadServiceGQL)
    readonly organizations = inject(OrganizationsService)
    protected readonly uploadService = inject(UploadGqlService)
    protected readonly matAssetsRenderingSvc = inject(MaterialAssetsRenderingService)
    protected readonly nameAssetFromSchemaSvc = inject(NameAssetFromSchemaService)
    private getMaterialWithOutput = async (
        materialListItem: MaterialBatchOperationFragment,
        materialOutputType: MaterialOutputType,
    ): Promise<BatchMaterialOutputFragment> => {
        const {material} = await fetchThrowingErrors(this.materialWithOutputGql)({
            id: materialListItem.id,
            materialOutputType,
        })

        return material
    }

    getExtraBatchActions = async (): Promise<ExtraBatchActionItem<MaterialsGridItemFragment>[]> => {
        const result: ExtraBatchActionItem<MaterialsGridItemFragment>[] = []

        if (this.$can().update.material(undefined, "batch")) {
            result.push({
                groupLabel: "Advanced",
                items: this.getAdvancedBatchItems(),
            })
        }

        const thumbnailBatchItems = this.getThumbnailBatchItems()
        const tileableBatchItems = this.getTileableBatchItems()

        const shaderBallBatchItems = this.getShaderBallBatchItems()
        const pbrExportBatchItems = this.getPbrExportBatchItems()
        const pConExportBatchItems = this.getPconExportBatchItems()

        if (this.$can().read.menu("batchStartMaterialImageJobs")) {
            result.push({
                groupLabel: "Generate",
                items: [
                    {...tileableBatchItems.create, label: "Tileable Image"},
                    {...shaderBallBatchItems.create, label: "Shader Ball"},
                    {groupLabel: "Flat Thumbnails", items: thumbnailBatchItems.create},
                    {groupLabel: "PBR Exports", items: pbrExportBatchItems.create},
                    {...pConExportBatchItems.create, label: "pCon Export"},
                ],
            })

            result.push({
                groupLabel: "Delete",
                items: [
                    {...tileableBatchItems.delete, label: "Tileable Image"},
                    {...shaderBallBatchItems.delete, label: "Shader Ball"},
                    {groupLabel: "Flat Thumbnails", items: thumbnailBatchItems.delete},
                    {groupLabel: "PBR Exports", items: pbrExportBatchItems.delete},
                    {...pConExportBatchItems.delete, label: "pCon Export"},
                ],
            })

            result.push({
                groupLabel: "Name",
                items: [
                    ...(tileableBatchItems.name ? [{...tileableBatchItems.name, label: "Tileable Image"}] : []),
                    ...(thumbnailBatchItems.name ? [{groupLabel: "Flat Thumbnails", items: thumbnailBatchItems.name}] : []),
                    ...(pbrExportBatchItems.name ? [{groupLabel: "PBR (.zip name only)", items: pbrExportBatchItems.name}] : []),
                ],
            })

            result.push({
                groupLabel: "Publish Material Outputs",
                items: this.getPublishBatchItems(),
            })
        }

        return result
    }

    private getPublishBatchItems = (): BatchMenuItem<MaterialBatchOperationFragment>["items"] => [
        {
            label: "Configura Material Database",
            text: "Submitted",
            batchOperation: async (materialListItems) => {
                const entries = await this.materialOutputsPublishingService.triggerConfiguraMaterialDatabasePublishBatch({
                    materialIds: materialListItems.map(({id}) => id),
                })
                const entryByMaterialId = new Map(entries.map((entry) => [entry.materialId, entry]))
                return materialListItems.map(({id, legacyId}) => {
                    const entry = entryByMaterialId.get(id)
                    if (!entry || entry.refusalMessage) {
                        this.logger.error(`Configura publish refused for material ${legacyId}: ${entry?.refusalMessage ?? "no result returned"}`)
                        return "errored"
                    }
                    return entry.jobId ? "succeeded" : "skipped"
                })
            },
        },
    ]

    getAdvancedActions = (fileInput: ElementRef) => {
        if (!this.$can().update.material(undefined, "batch")) return []
        return [
            {
                label: "Batch create materials",
                operation: () => {
                    fileInput.nativeElement.click()
                },
            },
        ]
    }

    /** Both export batches report the same way: counts in the snackbar, refusals in the console. */
    private exportBatchResult = (
        materialListItems: MaterialBatchOperationFragment[],
        entries: {materialId: string; outcome: MaterialExportOutcome; message?: string | null}[],
        label: string,
    ) => {
        const entryByMaterialId = new Map(entries.map((entry) => [entry.materialId, entry]))
        const counts = {Generated: 0, Regenerated: 0, AlreadyRunning: 0, UpToDate: 0, Refused: 0}
        const outcomes = materialListItems.map(({id, legacyId}): BatchItemOutcome => {
            const entry = entryByMaterialId.get(id)
            if (!entry || entry.outcome === MaterialExportOutcome.Refused) {
                this.logger.error(`${label} refused for material ${legacyId}: ${entry?.message ?? "no result returned"}`)
                counts.Refused += 1
                return "errored"
            }
            counts[entry.outcome] += 1
            return entry.outcome === MaterialExportOutcome.UpToDate ? "skipped" : "succeeded"
        })
        const summaryParts: string[] = []
        if (counts.Generated > 0) summaryParts.push(`${counts.Generated} generated`)
        if (counts.Regenerated > 0) summaryParts.push(`${counts.Regenerated} regenerated`)
        if (counts.AlreadyRunning > 0) summaryParts.push(`${counts.AlreadyRunning} already running`)
        if (counts.UpToDate > 0) summaryParts.push(`${counts.UpToDate} already current`)
        if (counts.Refused > 0) summaryParts.push(`${counts.Refused} failed (see console for details)`)
        return {outcomes, summary: `${label}: ${summaryParts.join(", ")}.`}
    }

    private safeCreateMaterialOutput = async (materialListItem: MaterialBatchOperationFragment, materialOutputType: MaterialOutputType) => {
        const material = await this.getMaterialWithOutput(materialListItem, materialOutputType)
        if (!material?.latestCyclesRevision || material.outputForType) {
            return false
        }

        await this.materialOutputService.createOutput({
            materialId: materialListItem.id,
            materialRevisionId: material.latestCyclesRevision.id,
            showNotification: false,
            type: materialOutputType,
            rethrowCaughtError: true,
        })

        return true
    }

    private safeDeleteMaterialOutput = async (materialListItem: MaterialBatchOperationFragment, materialOutputType: MaterialOutputType) => {
        const material = await this.getMaterialWithOutput(materialListItem, materialOutputType)
        if (!material.outputForType) {
            return false
        }
        await this.materialOutputService.cancelAndRemoveOutput({materialOutput: material.outputForType, showNotification: false})
        return true
    }

    private getThumbnailBatchItems = (): GetBatchOperationsItems<BatchMenuItem<MaterialBatchOperationFragment>["items"]> => {
        const createThumbnails: BatchMenuItem<MaterialBatchOperationFragment>["items"] = Array.from(FlatThumbnailOptionLabels.values()).map((flatOption) => ({
            label: flatOption.label,
            operation: async (materialListItem) => this.safeCreateMaterialOutput(materialListItem, flatOption.state.materialOutputType),
            tooltip: `Resolution: ${flatOption.state.resolution} x ${flatOption.state.resolution} px`,
        }))

        const deleteThumbnails: BatchMenuItem<MaterialBatchOperationFragment>["items"] = Array.from(FlatThumbnailOptionLabels.values()).map((flatOption) => ({
            label: `Delete ${flatOption.label}`,
            operation: async (materialListItem) => this.safeDeleteMaterialOutput(materialListItem, flatOption.state.materialOutputType),
        }))

        const nameThumbnails: BatchMenuItem<MaterialBatchOperationFragment>["items"] = Array.from(FlatThumbnailOptionLabels.values()).map((flatOption) => ({
            label: `Name ${flatOption.label} from schema`,
            operation: async (materialListItem) => {
                const material = await this.getMaterialWithOutput(materialListItem, flatOption.state.materialOutputType)
                if (!material.outputForType || !material.outputForType.result?.id) {
                    return false
                }
                await this.nameAssetFromSchemaSvc.renameRenderFromSchema(materialListItem.id, flatOption.state.dataObjectAssignmentType)
                return true
            },
        }))

        return {
            create: createThumbnails,
            delete: deleteThumbnails,
            name: nameThumbnails,
        }
    }

    private getTileableBatchItems = (): GetBatchOperationsItems<BatchMenuItem<MaterialBatchOperationFragment>["items"][0]> => {
        return {
            create: {
                label: "Create Tileable",
                operation: async (materialListItem) => this.safeCreateMaterialOutput(materialListItem, MaterialOutputType.TileableImage),
            },
            delete: {
                label: "Delete tileable",
                operation: async (materialListItem) => this.safeDeleteMaterialOutput(materialListItem, MaterialOutputType.TileableImage),
            },
            name: {
                label: "Name tileable from schema",
                operation: async (materialListItem) => {
                    const material = await this.getMaterialWithOutput(materialListItem, MaterialOutputType.TileableImage)
                    if (!material.outputForType || !material.outputForType.result?.id) {
                        return false
                    }
                    await this.nameAssetFromSchemaSvc.renameRenderFromSchema(materialListItem.id, DataObjectAssignmentType.MaterialTileableRender)
                    return true
                },
            },
        }
    }

    private getShaderBallBatchItems = (): GetBatchOperationsItems<BatchMenuItem<MaterialBatchOperationFragment>["items"][0]> => {
        return {
            create: {
                label: "Create shader ball",
                operation: async (materialListItem) => this.safeCreateMaterialOutput(materialListItem, MaterialOutputType.ShaderBall),
            },
            delete: {
                label: "Delete shader ball",
                operation: async (materialListItem) => this.safeDeleteMaterialOutput(materialListItem, MaterialOutputType.ShaderBall),
            },
        }
    }

    private getPbrExportBatchItems = (): GetBatchOperationsItems<BatchMenuItem<MaterialBatchOperationFragment>["items"]> => {
        const createExport: BatchMenuItem<MaterialBatchOperationFragment>["items"] = getDefaultPbrExportOptions().map((exportOption) => ({
            label: exportOption.displayName,
            batchOperation: async (materialListItems) =>
                this.exportBatchResult(
                    materialListItems,
                    await this.materialOutputService.ensurePbrExportBatch(
                        materialListItems.map(({id}) => id),
                        exportOption.pbrExportType,
                    ),
                    `${exportOption.displayName} exports`,
                ),
        }))

        const deleteExport: BatchMenuItem<MaterialBatchOperationFragment>["items"] = getDefaultPbrExportOptions().map((exportOption) => ({
            label: `Delete ${exportOption.displayName}`,
            operation: async (materialListItem) => this.safeDeleteMaterialOutput(materialListItem, exportOption.materialOutputType),
        }))

        const nameExport: BatchMenuItem<MaterialBatchOperationFragment>["items"] = Array.from(PbrExportOptionLabels.entries()).map(
            ([exportType, exportOption]) => ({
                label: `Name ${exportOption.label} from schema`,
                operation: async (materialListItem) => {
                    const material = await this.getMaterialWithOutput(materialListItem, exportType)
                    if (!material.outputForType || !material.outputForType.result?.id || !material.outputForType.config?.content) {
                        return false
                    }
                    await this.nameAssetFromSchemaSvc.renameExportFromSchema(materialListItem.id, exportType)
                    return true
                },
            }),
        )

        return {
            create: createExport,
            delete: deleteExport,
            name: nameExport,
        }
    }

    private getPconExportBatchItems = (): GetBatchOperationsItems<BatchMenuItem<MaterialBatchOperationFragment>["items"][0]> => {
        return {
            create: {
                label: "Create pCon",
                batchOperation: async (materialListItems) =>
                    this.exportBatchResult(
                        materialListItems,
                        await this.materialOutputService.ensurePconExportBatch(materialListItems.map(({id}) => id)),
                        "pCon exports",
                    ),
            },
            delete: {
                label: "Delete pCon",
                operation: async (materialListItem) => this.safeDeleteMaterialOutput(materialListItem, MaterialOutputType.PconExport),
            },
        }
    }

    private getAdvancedBatchItems = (): BatchMenuItem<MaterialBatchOperationFragment>["items"] => {
        const setArticleId = async (materialListItem: MaterialBatchOperationFragment, test: boolean) => {
            const {
                material: {tagAssignments: materialRangeTagAssignments},
            } = await fetchThrowingErrors(this.materialWithRangeTagsGql)({legacyId: materialListItem.legacyId})
            const materialRangeTag: BasicTagInfoFragment | undefined = materialRangeTagAssignments?.[0]?.tag

            const regex = /\b\d{3}\b/
            const filteredText = materialListItem.name?.match(regex)
            if (materialRangeTag && materialRangeTag.description && filteredText?.length == 1) {
                if (test) {
                    return false
                }
                await mutateThrowingErrors(this.updateMaterialGql)({
                    input: {id: materialListItem.id, articleId: materialRangeTag.description + filteredText[0]},
                })
                return true
            } else {
                this.logger.debug(`${materialListItem.legacyId} -> Article ID could not be determined`)
                return false
            }
        }
        return [
            {
                label: "Fill article ID (test)",
                operation: async (item) => setArticleId(item, true),
            },
            {
                label: "Fill article ID",
                operation: async (item) => setArticleId(item, false),
            },
        ]
    }

    private getBatchDownloadDetail = async (
        materialListItem: MaterialBatchOperationFragment,
        dataObjectAssignment: DataObjectAssignmentType,
    ): Promise<ImageDownloadDetailsFragment> => {
        const material = (
            await fetchThrowingErrors(this.imageDownloadDetailsGql)({
                materialId: materialListItem.id,
                assignmentType: dataObjectAssignment,
            })
        ).material

        if (material.dataObjectAssignments.length === 0) throw new Error(`No ${dataObjectAssignment} found for material ${materialListItem.id}`)
        if (material.dataObjectAssignments.length > 1)
            this.logger.warn(`Multiple ${dataObjectAssignment} assignments found for material ${materialListItem.id}`)

        return material.dataObjectAssignments[0].dataObject
    }

    private makeDownloadItem = async (materialDownloadData: DataObjectDetailsFragment | undefined | null) => {
        if (!materialDownloadData) throw new Error("No data for download found")
        const path = materialDownloadData.originalFileName
        return [{path: path, dataObjectLegacyId: materialDownloadData.legacyId}]
    }

    getBatchDownloadData = (): BatchDownloadData<MaterialBatchOperationFragment>[] => {
        if (!this.$can().read.menu("batchDownloadMaterials")) return []

        const tileableImageJpegExports: BatchDownloadData<MaterialBatchOperationFragment>[] = Object.values(DownloadResolution).map((resolution) => {
            return {
                pathInMenu: `Tileable Image/JPEG/${mapDownloadResolutionToImageResolution(resolution)}`,
                getDataObjectsForDownload: async (materialListItem: MaterialBatchOperationFragment) => {
                    const batchDownloadDetails = await this.getBatchDownloadDetail(materialListItem, DataObjectAssignmentType.MaterialTileableRender)
                    if (batchDownloadDetails.mediaType === "application/zip") throw new Error("No jpeg data for download found")
                    return this.makeDownloadItem(batchDownloadDetails[`jpeg${resolution}`])
                },
            }
        })

        const tileableImageTiffExport: BatchDownloadData<MaterialBatchOperationFragment> = {
            pathInMenu: "Tileable Image/TIFF",
            getDataObjectsForDownload: async (materialListItem: MaterialBatchOperationFragment) => {
                const batchDownloadDetails = await this.getBatchDownloadDetail(materialListItem, DataObjectAssignmentType.MaterialTileableRender)
                if (batchDownloadDetails.mediaType === "application/zip") throw new Error("No tiff data for download found")
                return this.makeDownloadItem(batchDownloadDetails.tiff)
            },
        }

        const thumbnailExports: BatchDownloadData<MaterialBatchOperationFragment>[] = Array.from(FlatThumbnailOptionLabels.values()).flatMap((flatOption) => {
            const jpegPaths = Object.values(DownloadResolution).map((resolution) => ({
                pathInMenu: `Flat Thumbnails/${flatOption.label}/JPEG/${mapDownloadResolutionToImageResolution(resolution)}`,
                getDataObjectsForDownload: async (materialListItem: MaterialBatchOperationFragment) => {
                    const batchDownloadDetails = await this.getBatchDownloadDetail(materialListItem, flatOption.state.dataObjectAssignmentType)
                    return this.makeDownloadItem(batchDownloadDetails[`jpeg${resolution}`])
                },
            }))

            const tiffPath = {
                pathInMenu: `Flat Thumbnails/${flatOption.label}/TIFF`,
                getDataObjectsForDownload: async (materialListItem: MaterialBatchOperationFragment) => {
                    const batchDownloadDetails = await this.getBatchDownloadDetail(materialListItem, flatOption.state.dataObjectAssignmentType)
                    return this.makeDownloadItem(batchDownloadDetails.tiff)
                },
            }

            return [...jpegPaths, tiffPath]
        })

        const downloadableExportFormats: MaterialMapsExporterCore.Types.Format[] = ["jpeg", "tiff"]
        const pbrExports: BatchDownloadData<MaterialBatchOperationFragment>[] = getDefaultPbrExportOptions()
            .filter((exportOption) => downloadableExportFormats.includes(exportOption.format))
            .map((exportOption) => ({
                pathInMenu: "PBR Exports/" + exportOption.displayName,
                getDataObjectsForDownload: async (materialListItem: MaterialBatchOperationFragment) => {
                    const materialDetail = await this.getMaterialWithOutput(materialListItem, exportOption.materialOutputType)
                    const pbrOutput = materialDetail.outputForType
                    if (!pbrOutput || !pbrOutput.result) throw new Error(`No ${exportOption.displayName} export found for material`)
                    return this.makeDownloadItem(pbrOutput.result)
                },
            }))

        const pConExport: BatchDownloadData<MaterialBatchOperationFragment> = {
            pathInMenu: "pCon Export",
            getDataObjectsForDownload: async (materialListItem: MaterialBatchOperationFragment) => {
                const materialDetail = await this.getMaterialWithOutput(materialListItem, MaterialOutputType.PconExport)
                const pConOutput = materialDetail.outputForType
                if (!pConOutput || !pConOutput.result) throw new Error("No pCon export found for material")
                return this.makeDownloadItem(pConOutput.result)
            },
        }

        return [...tileableImageJpegExports, tileableImageTiffExport, ...thumbnailExports, ...pbrExports, pConExport]
    }
}
